export type EnrollmentParentKey = 'father' | 'mother';

export type EnrollmentParentInfoInput = {
  firstName?: unknown;
  lastName?: unknown;
  title?: unknown;
  occupation?: unknown;
  workPhone?: unknown;
  cellPhone?: unknown;
  email?: unknown;
};

export type EnrollmentSignatureInput = {
  fatherSignature?: unknown;
  motherSignature?: unknown;
};

export type EnrollmentSignatureRequirement = {
  requiredParents: EnrollmentParentKey[];
  missingParents: EnrollmentParentKey[];
  valid: boolean;
  message: string | null;
};

const readText = (value: unknown): string => String(value ?? '').trim();

export const hasCompleteEnrollmentParentName = (parent: EnrollmentParentInfoInput): boolean => (
  Boolean(readText(parent.firstName) && readText(parent.lastName))
);

export const hasEnrollmentParentProfileInformation = (
  parent: EnrollmentParentInfoInput,
  options?: { ignoredEmail?: unknown },
): boolean => {
  const email = readText(parent.email);
  const ignoredEmail = readText(options?.ignoredEmail).toLowerCase();
  const emailCountsAsInformation = Boolean(email && email.toLowerCase() !== ignoredEmail);
  return Boolean(
    readText(parent.firstName)
    || readText(parent.lastName)
    || readText(parent.title)
    || readText(parent.occupation)
    || readText(parent.workPhone)
    || readText(parent.cellPhone)
    || emailCountsAsInformation
  );
};

export const requiresEnrollmentParentName = (
  parent: EnrollmentParentInfoInput,
  options?: { ignoredEmail?: unknown },
): boolean => (
  hasEnrollmentParentProfileInformation(parent, options) && !hasCompleteEnrollmentParentName(parent)
);

const hasSignature = (value: unknown): boolean => Boolean(readText(value));

const parentLabel = (parent: EnrollmentParentKey): string => (
  parent === 'father' ? "Father's" : "Mother's"
);

export const getListedEnrollmentParents = (parents: {
  father: EnrollmentParentInfoInput;
  mother: EnrollmentParentInfoInput;
}): EnrollmentParentKey[] => {
  const listedParents: EnrollmentParentKey[] = [];
  if (hasCompleteEnrollmentParentName(parents.father)) listedParents.push('father');
  if (hasCompleteEnrollmentParentName(parents.mother)) listedParents.push('mother');
  return listedParents;
};

export const resolveEnrollmentSignatureRequirement = (input: {
  father: EnrollmentParentInfoInput;
  mother: EnrollmentParentInfoInput;
  signatures: EnrollmentSignatureInput;
}): EnrollmentSignatureRequirement => {
  const listedParents = getListedEnrollmentParents({
    father: input.father,
    mother: input.mother,
  });

  if (listedParents.length === 0) {
    const hasAnySignature = hasSignature(input.signatures.fatherSignature)
      || hasSignature(input.signatures.motherSignature);
    return {
      requiredParents: [],
      missingParents: [],
      valid: hasAnySignature,
      message: hasAnySignature
        ? null
        : 'At least one parent signature is required before submitting registration.',
    };
  }

  const missingParents = listedParents.filter((parent) => (
    parent === 'father'
      ? !hasSignature(input.signatures.fatherSignature)
      : !hasSignature(input.signatures.motherSignature)
  ));

  const message = (() => {
    if (missingParents.length === 0) return null;
    if (missingParents.length === 2) {
      return 'Both listed parents must sign before submitting registration.';
    }
    const missingParent = missingParents[0];
    return `${parentLabel(missingParent)} signature is required because ${missingParent} information was provided.`;
  })();

  return {
    requiredParents: listedParents,
    missingParents,
    valid: missingParents.length === 0,
    message,
  };
};
